import { Alert, Flex, Form, Input, Modal, Select, Space, Table, Tag, Tooltip } from "antd";
import { Breadcrumb, Button, Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import AppSearch from "../../../components/common/AppSearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useCallback, useEffect, useMemo, useState } from "react";
import IconButton from "../../../components/common/IconButton";
import { useToasts } from "../../../contexts/toasts";
import { useFullScreenLoader } from "../../../contexts/fullScreenLoader";
import { RedisVariable, RedisVariables, RedisVariableType } from "../../../api/RedisVariables";
import { debounce } from "lodash";
import { Option, PagedResults } from "../../../models/generics";
import ScriptEditor from "../../../components/common/ScriptEditor";
import { getStringType } from "../../../utils";


const typeOptions = Object.entries(RedisVariableType).map(([label, value]) => ({ label, value }));

export default function RedisVariablesList() {
  const [error, setError] = useState(false);
  const [keyFilter, setKeyFilter] = useState('');
  const [namespaceFilter, setNamespaceFilter] = useState('');
  const [namespacesOptions, setNamespacesOptions] = useState<Option[]>();
  const [redisVariables, setRedisVariables] = useState<RedisVariable[]>();
  const [variable, setVariable] = useState<RedisVariable>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [form] = Form.useForm();
  const [totalVariables, setTotalVariables] = useState(0);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const getNamespaces = useCallback(() => {
    RedisVariables.getNamespaces()
      .then(
        ({ data }) => {
          setNamespacesOptions(data.map((opt) => ({
            value: opt,
            label: opt
          })))
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('redis-namespaces-error', message, `Redis Namespaces Error ${status ? `(${status})` : ''}`, 'danger');
          setNamespacesOptions([]);
        }
      )
  }, [showToast]);

  const getRedisVariables = useCallback(() => {
    const params = new URLSearchParams([
      ['page', `${currentPage}`],
      ['size', `${10}`],
    ]);
    if (namespaceFilter?.length) params.set("namespace", namespaceFilter);
    if (keyFilter?.length) params.set("filter", keyFilter);

    RedisVariables.getRedisVariables(params)
      .then(
        ({ data }) => {
          setRedisVariables((data as PagedResults<RedisVariable>).content);
          setTotalVariables((data as PagedResults<RedisVariable>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('redis-variables-error', message, `Redis Variables Error ${status ? `(${status})` : ''}`, 'danger');
          setRedisVariables([]);
          setError(true);
        }
      );
  }, [showToast, currentPage, namespaceFilter, keyFilter]);

  const reset = useCallback(() => {
    setError(false);
    setRedisVariables(undefined);
    setNamespacesOptions(undefined);
    getNamespaces();
    getRedisVariables();
  }, [getRedisVariables, getNamespaces]);

  useEffect(() => {
    form.setFieldValue('type', 'string');
  }, [form]);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (currentPage > 1) setCurrentPage(1);
    else reset();
    // eslint-disable-next-line
  }, [namespaceFilter, keyFilter]);

  const handleCreateModalClose = useCallback(() => {
    setShowCreateModal(false);
    form.resetFields();
    setVariable(undefined);
  }, [form]);

  const retrieveVariableDetail = useCallback((variable: RedisVariable) => {
    RedisVariables.getRedisVariable(variable.key, variable.namespace)
      .then(
        ({ data }) => {
          const type = getStringType(data.value);
          const variable = {
            ...data,
            type: type === 'json' ? RedisVariableType.JSON : RedisVariableType.String,
          };
          form.setFieldsValue(variable);
          setVariable(variable);
        },
        (err) => {
          showToast('redis-variable-error', 'Could not get Redis variable', 'Redis Variable Error', 'danger');
          handleCreateModalClose();
          setVariable(undefined);
        }
      );
  }, [showToast, handleCreateModalClose, form]);

  const handleFilterChange = useCallback((filter: 'namespace' | 'key') => (evt: string) => {
    if (filter === 'namespace') setNamespaceFilter(evt);
    if (filter === 'key') setKeyFilter(evt);
  }, []);

  const debounceKeyChange = useMemo(() => debounce(handleFilterChange("key"), 500), [handleFilterChange]);

  useEffect(() => {
    return () => {
      debounceKeyChange.cancel();
    }
  }, [debounceKeyChange]);

  const openCreateModal = useCallback(() => setShowCreateModal(true), []);
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const handleScriptChange = useCallback((value?: string) => form.setFieldValue('value', value), [form]);

  const handleDeleteModalClose = useCallback(() => {
    setVariable(undefined);
    setShowDeleteModal(false);
  }, []);

  const openDeleteModal = useCallback((variable: RedisVariable) => () => {
    setVariable(variable);
    setShowDeleteModal(true);
  }, []);

  const openEditModal = useCallback((variable: RedisVariable) => () => {
    retrieveVariableDetail(variable);
    setShowCreateModal(true);
  }, [retrieveVariableDetail]);

  const handleRedisFormSubmit = useCallback((values: any) => {
    showLoader();

    const promise = variable !== undefined
      ? RedisVariables.updateRedisVariable(values)
      : RedisVariables.createRedisVariable(values);

    promise.then(
      () => {
        hideLoader();
        showToast('redis-variable-update-success', `Variable ${variable ? 'updated' : 'created'} successfully`, 'Redis Variable Success', 'success');
        handleCreateModalClose();
        reset();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('redis-variable-update-error', message, `Redis Variable Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [variable, showLoader, hideLoader, reset, handleCreateModalClose, showToast]);

  const deleteRedisVariable = useCallback(() => {
    showLoader();

    RedisVariables.deleteRedisVariable(variable!.key, variable!.namespace)
      .then(
        () => {
          hideLoader();
          showToast('redis-variable-delete-success', 'Redis Variable deleted successfully', 'Redis Variable Delete Success', 'success');
          reset();
          handleDeleteModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('redis-variable-delete-error', message, `Redis Variable Delete Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [variable, showLoader, hideLoader, showToast, reset, handleDeleteModalClose]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving Redis variables. Try again later."
      />
    )

    return (
      <Table
        size="middle"
        scroll={{ y: 'calc(100vh - 300px)' }}
        dataSource={redisVariables}
        loading={!redisVariables}
        rowKey="key"
        pagination={{
          pageSize: 10,
          current: currentPage,
          total: totalVariables,
          showSizeChanger: false,
          onChange: handlePageChange,
        }}
      >
        <Table.Column
          key="key"
          dataIndex="key"
          title="Variable Key"
          render={(value, record) => (
            <Flex className="overflow-hidden" align="center">
              {record.namespace && record.namespace !== "" && (
                <Tooltip title="Namespace">
                  <Tag color="geekblue" className="font-sm">{record.namespace}</Tag>
                </Tooltip>
              )}
              <div className="text-truncate">
                <Tooltip title={value} mouseEnterDelay={0.5}>
                  <span className="font-monospace">{value}</span>
                </Tooltip>
              </div>
            </Flex>
          )}
        />
        <Table.Column
          width="10%"
          key="actions"
          title="Actions"
          className="text-center"
          render={(_value, record: any) => (
            <Space size={5}>
              <IconButton tooltip="Edit" onClick={openEditModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('pen')} />
              </IconButton>
              <IconButton tooltip="Delete" className="text-danger" onClick={openDeleteModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('trash')} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    )
  }, [error, redisVariables, openEditModal, currentPage, totalVariables, handlePageChange, openDeleteModal]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Redis Variables Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3 align-items-center">
        <Col>
          <h5 className="mb-0">Redis Variables List</h5>
        </Col>

        <Col md={2} className="px-3">
          <Select
            allowClear
            loading={!namespacesOptions}
            placeholder="Select Namespace"
            onChange={handleFilterChange("namespace")}
            options={namespacesOptions}
            className="w-100"
          />
        </Col>

        <Col className="text-center px-3">
          <AppSearch placeholder="Search key..." onChange={debounceKeyChange} />
        </Col>

        <Col md={3} className="text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register Variable</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal
        title="Delete Redis Variable"
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        footer={
          <Flex justify="space-between" className="mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteRedisVariable}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </Flex>
        }
      >
        <p>Are you sure you want to delete variable <b>{variable?.key}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>

      <Modal
        title={`${variable ? "Edit" : "Create"} Redis Variable`}
        open={showCreateModal}
        onCancel={handleCreateModalClose}
        afterClose={handleCreateModalClose}
        footer={null}
        style={{ top: 30 }}
        width={600}
      >
        <Form layout="vertical" form={form} onFinish={handleRedisFormSubmit}>
          <Form.Item name="namespace" label="Namespace">
            <Input disabled={!!variable} placeholder="Variable Namespace" />
          </Form.Item>

          <Form.Item name="key" label="Key" rules={[{ required: true, message: "Variable Key is required." }]}>
            <Input disabled={!!variable} placeholder="Variable Key" />
          </Form.Item>

          <Form.Item name="type" label="Type">
            <Select placeholder="Select Variable type" options={typeOptions} />
          </Form.Item>

          <Form.Item noStyle dependencies={['type']}>
            {({ getFieldValue }) => {
              const type = getFieldValue('type'), value = getFieldValue('value');
              return (
                <Form.Item
                  name="value"
                  label="Value"
                  rules={type === 'JSON' ? undefined : [{ required: true, message: "Variable Value is required." }]}
                >
                  {type === 'JSON'
                    ? <ScriptEditor value={value} height={250} onChange={handleScriptChange} />
                    : <Input.TextArea rows={4} placeholder="Variable Value" />
                  }
                </Form.Item>
              )
            }}
          </Form.Item>

          <br />

          <Flex justify="space-between">
            <Button size="sm" type="button" variant="light" className="border" onClick={handleCreateModalClose}>Cancel</Button>
            <Button size="sm" type="submit" variant="success">
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Confirm</span>
            </Button>
          </Flex>
        </Form>
      </Modal>
    </Container>
  )
}